import { createFileRoute } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import {
  GuideHero,
  GuideBody,
  GuideFaq,
  GuideRelated,
  CheckList,
  KeyStats,
  Callout,
  ResponsiveTable,
  StepProcess,
  buildGuideHead,
  type GuideMeta,
} from "@/components/GuideLayout";
import { Link } from "@tanstack/react-router";

const meta: GuideMeta = {
  category: "Referensguide · Standarder",
  title: "EN 12600 — skyddsklasser för säkerhetsglas och säkerhetsfilm förklarade",
  tldr:
    "EN 12600 är den europeiska standarden som klassar hur glas (med eller utan säkerhetsfilm) beter sig när en människokropp slår emot det. Klassningen skrivs som tre tecken (t.ex. 2B2) och beskriver fallhöjd vid pendelslagstest, brottmönster och splitterspridning. Denna guide förklarar vad varje klass innebär i praktiken, hur testet går till och vilken klass som typiskt krävs för olika verksamheter — utan att rekommendera en specifik produkt.",
  readTimeMin: 9,
  heroImage: IMAGES.sakerhetsfilm,
  path: "/sakerhetsfilm-skyddsklass-en12600-guide",
  updated: "2026-06-16",
  faqs: [
    {
      q: "Vad betyder klassningen 2B2 i EN 12600?",
      a: "Varje tecken har en betydelse. Första siffran (1, 2 eller 3) anger högsta fallhöjd där glaset klarade pendelslagstestet — 1 motsvarar 1200 mm (högst påfrestning), 2 motsvarar 450 mm, 3 motsvarar 190 mm. Bokstaven (A, B eller C) beskriver brottmönstret — A = många små bitar, B = stora skärvor som hänger ihop (typiskt för laminerat glas och glas med säkerhetsfilm), C = härdat glas som faller i små bitar. Sista siffran upprepar den högsta klarade fallhöjden. 2B2 betyder alltså: glaset klarade 450 mm pendelfall och splittrades i sammanhållna stora bitar.",
    },
    {
      q: "Hur går pendelslagstestet i EN 12600 till?",
      a: "Glaspanelen (1938 × 876 mm) monteras vertikalt i en testram. En tvillinghjuls­pendel med en stötkropp på 50 kg som efterliknar en mänsklig kropp släpps från en bestämd höjd — 190, 450 eller 1200 mm — och slår mot glasets mitt. Provet utvärderas på två kriterier: om glaset alls bryts vid given höjd, och hur det bryts (brottmönstret). Glaset klassificeras efter den högsta fallhöjd det klarar utan farligt brottmönster.",
    },
    {
      q: "Vilken EN 12600-klass krävs på en arbetsplats enligt AFS 2020:1?",
      a: "Arbetsmiljöverkets föreskrift AFS 2020:1 kräver att glas som människor kan kollidera med ska vara utfört så att personskaderisken minimeras — vilket typiskt uppfylls med minst klass 2B2 i utsatta lägen (entréer, glaspartier upp till cirka 1,5 m höjd, glasdörrar). Föreskriften pekar inte ut en specifik klass i alla situationer utan ställer ett funktionskrav; klass 2B2 är vad fastighetsansvariga och försäkringsbolag i praktiken brukar acceptera som tillräckligt.",
    },
    {
      q: "Är EN 12600 samma sak som EN 356?",
      a: "Nej. EN 12600 mäter personsäkerhet vid mänsklig stöt (kollision). EN 356 mäter manuell attack med slägga och yxa (inbrottsförsök) och klassas P1A–P5A respektive P6B–P8B. Ett glas eller en filmlösning kan certifieras enligt båda standarderna parallellt, men de mäter olika hot. EN 12600 är arbetsmiljö- och personskadestandard; EN 356 är inbrotts- och egendomsskydds­standard.",
    },
    {
      q: "Höjer säkerhetsfilm en standardrutas EN 12600-klassning?",
      a: "Ja, det är en av de vanligaste anledningarna att installera säkerhetsfilm i befintliga byggnader. En oklassad floatglas­ruta kan typiskt uppgraderas till klass 2B2 med en korrekt monterad säkerhetsfilm av rätt tjocklek. För klass 1B1 krävs kraftigare film och ofta förankring i karm. Klassningen gäller filmen monterad på det specifika glaset enligt tillverkarens testprotokoll — inte filmen som lös produkt.",
    },
    {
      q: "Vilken klass behöver en skola, ett kontor eller en bank?",
      a: "Skolor, förskolor, vårdmiljö och offentliga lokaler: klass 2B2 är den praxis som rekommenderas för glaspartier i kollisionszon. Vanliga kontor: 2B2 i entréer och utsatta glaspartier räcker i de flesta fall. Butiker med exponerade varor och bankkontor: klass 1B1 är standard för skyltfönster, ofta i kombination med EN 356 P2A–P4A för inbrottsfördröjning. Högriskmiljöer (juvelerare, vapenförvaring, ambassader): klass 1B1 plus EN 356 P5A–P8B och karm­förankring.",
    },
  ],
  related: [
    { to: "/sakerhetsfilm", label: "Säkerhetsfilm — produktöversikt & offert" },
    { to: "/guider/sakerhetsfilm-inbrottsskydd", label: "Säkerhetsfilm — inbrottsskydd & AFS 2020:1" },
    { to: "/ordlista", label: "Ordlista — EN 12600, EN 356, AFS och fler termer" },
  ],
};

export const Route = createFileRoute("/sakerhetsfilm-skyddsklass-en12600-guide")({
  head: () => buildGuideHead(meta),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      <GuideHero meta={meta} />
      <GuideBody>
        <KeyStats
          items={[
            { value: "1200 mm", label: "Fallhöjd klass 1", sub: "högsta påfrestning" },
            { value: "450 mm", label: "Fallhöjd klass 2", sub: "praxis i arbetsmiljö" },
            { value: "190 mm", label: "Fallhöjd klass 3", sub: "grundnivå" },
            { value: "50 kg", label: "Pendelvikt", sub: "efterliknar människokropp" },
          ]}
          source="SS-EN 12600:2002 — Glass in building. Pendulum test."
        />

        <Callout variant="info" title="Den här sidan är en referensguide — inte en offertsida">
          <p>
            Här förklarar vi vad EN 12600-klasserna innebär, hur de testas och
            vilken klass som typiskt krävs i olika verksamheter. För en konkret offert,
            produktrekommendation eller frågor om garanti, se{" "}
            <Link to="/sakerhetsfilm" className="underline decoration-gold/40 hover:text-foreground">
              /sakerhetsfilm
            </Link>
            . För en bredare genomgång av inbrottsskydd och AFS 2020:1, se{" "}
            <Link to="/guider/sakerhetsfilm-inbrottsskydd" className="underline decoration-gold/40 hover:text-foreground">
              guiden Säkerhetsfilm & inbrottsskydd
            </Link>
            .
          </p>
        </Callout>

        <h2>Vad mäter EN 12600?</h2>
        <p>
          EN 12600 (i Sverige SS-EN 12600) är den europeiska standarden för{" "}
          <strong>pendelslagstest av glas i byggnader</strong>. Den mäter hur glaset
          beter sig när en människokropp slår mot det, till exempel en person som faller eller
          springer in i ett glasparti.
        </p>
        <p>
          Standarden mäter <em>inte</em> inbrottsmotstånd (det gör EN 356), inte
          brandmotstånd (EN 1364), inte ljudisolering (EN ISO 10140) och inte motstånd
          mot vinklade projektiler (EN 1063). EN 12600 svarar bara på en fråga:{" "}
          <strong>vad händer om en människokropp slår mot glaset?</strong>
        </p>

        <h2>Så läser du klassningens tre tecken</h2>
        <p>
          En EN 12600-klassning skrivs alltid med tre tecken: siffra, bokstav, siffra
          (t.ex. <strong>2B2</strong> eller <strong>1B1</strong>). Varje tecken har en
          egen betydelse:
        </p>

        <ResponsiveTable>
          <thead>
            <tr>
              <th>Position</th>
              <th>Möjliga värden</th>
              <th>Vad det betyder</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Första siffran</td>
              <td>1, 2 eller 3</td>
              <td>
                Högsta fallhöjd glaset klarade vid testet utan farligt brottmönster.
                <strong> 1</strong> = 1200 mm (högst påfrestning),{" "}
                <strong>2</strong> = 450 mm, <strong>3</strong> = 190 mm.
              </td>
            </tr>
            <tr>
              <td>Bokstaven</td>
              <td>A, B eller C</td>
              <td>
                Brottmönster. <strong>A</strong> = många separata bitar (typiskt vanligt
                glas som spricker farligt). <strong>B</strong> = stora skärvor som hänger
                ihop på filmen eller laminatet (säkert). <strong>C</strong> = härdat glas
                som faller i små, trubbiga bitar.
              </td>
            </tr>
            <tr>
              <td>Sista siffran</td>
              <td>1, 2 eller 3</td>
              <td>
                Upprepar den högsta fallhöjd glaset klarade. Anledningen att den finns med
                separat är att testet utförs i flera steg — den sista siffran tydliggör
                slutprovet.
              </td>
            </tr>
          </tbody>
        </ResponsiveTable>

        <p>
          För säkerhetsfilm är <strong>B</strong> alltid målet. Det är det
          beteendet (skärvor som hänger ihop) som filmen tillför ett vanligt glas. Vilken
          siffra man når beror på film­tjockleken och glas­typen tillsammans.
        </p>

        <h2>Hur pendelslagstestet går till</h2>
        <StepProcess
          steps={[
            {
              title: "1. Glaspanelen monteras",
              body:
                "En glaspanel på 1938 × 876 mm fixeras vertikalt i en stel testram. För säkerhetsfilm monteras filmen enligt tillverkarens spec på det specifika glaset som ska klassificeras.",
            },
            {
              title: "2. Stötkroppen släpps från given höjd",
              body:
                "En tvillinghjulspendel med en 50 kg tung stötkropp (konstruerad för att efterlikna en mänsklig kropp) släpps från en av de tre fallhöjderna — 190, 450 eller 1200 mm — och slår mot panelens mitt.",
            },
            {
              title: "3. Resultatet utvärderas på två kriterier",
              body:
                "(1) Bröts glaset alls vid given höjd? (2) Om ja — vilket brottmönster (A, B eller C)? Skärvor större än en viss vikt eller skarpkantade fragment som lossnar = underkänt.",
            },
            {
              title: "4. Klassningen tilldelas",
              body:
                "Glaset/filmkombinationen klassas efter den högsta fallhöjd den klarade utan farligt brottmönster, samt det brottmönster den uppvisade. Klassningen gäller den specifika glas- och filmkombinationen som testades — inte filmen som lös produkt.",
            },
          ]}
        />

        <h2>Klassjämförelse — vad varje klass innebär i praktiken</h2>
        <ResponsiveTable>
          <thead>
            <tr>
              <th>EN 12600-klass</th>
              <th>Klarad pendelhöjd</th>
              <th>Vad det motsvarar i praktiken</th>
              <th>Typisk användning</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>3B3</strong></td>
              <td>190 mm</td>
              <td>
                Grundläggande splitterskydd. Glaset hänger ihop vid lätt stöt eller fall i
                låg höjd. Räcker för att glas inte ska regna in i rummet om det krossas.
              </td>
              <td>
                Inre glaspartier utan kollisionsrisk, glas i låg höjd, lägre prioritets­objekt.
              </td>
            </tr>
            <tr>
              <td><strong>2B2</strong></td>
              <td>450 mm</td>
              <td>
                Klarar en vuxen person som faller mot glaset i normal hastighet. Skärvorna
                hänger samman på filmen. Ingen lossnar och faller ut.
              </td>
              <td>
                Praxis för glaspartier i kollisionszon i kontor, skolor, vård, offentliga
                lokaler och kommersiella entréer (uppfyller typiskt AFS 2020:1).
              </td>
            </tr>
            <tr>
              <td><strong>1B1</strong></td>
              <td>1200 mm</td>
              <td>
                Klarar en vuxen person som springer in i glaset i full hastighet eller en
                större stöt. Filmen håller ihop även vid kraftig påfrestning.
              </td>
              <td>
                Butiker med exponerade varor, bankkontor, skyltfönster i utsatta lägen,
                högriskmiljö. Ofta kombinerad med EN 356 P2A–P5A för inbrottsfördröjning.
              </td>
            </tr>
          </tbody>
        </ResponsiveTable>

        <h2>Vilken klass krävs i din verksamhet?</h2>
        <p>
          Tabellen ger vägledning om vad som brukar krävas eller rekommenderas i Sverige.
          För bindande råd behöver du alltid stämma av med byggherren och försäkringsbolaget
          och kontrollera Arbetsmiljöverkets föreskrifter.
        </p>
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Verksamhet</th>
              <th>Typisk minimiklass</th>
              <th>Skäl</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Kontor — innerglas utan kollisionsrisk</td>
              <td>3B3</td>
              <td>Grundläggande splitterskydd vid kross.</td>
            </tr>
            <tr>
              <td>Kontor — entréer, glaspartier i kollisionszon</td>
              <td>2B2</td>
              <td>Uppfyller praxis för AFS 2020:1 i utsatta lägen.</td>
            </tr>
            <tr>
              <td>Skola, förskola, vårdmiljö</td>
              <td>2B2</td>
              <td>Hög personrörelse, barn och äldre — kollisionsrisk högre.</td>
            </tr>
            <tr>
              <td>Butik — vanlig (kläder, böcker, café)</td>
              <td>2B2</td>
              <td>Personsäkerhet primärt, inbrottsskydd sekundärt.</td>
            </tr>
            <tr>
              <td>Butik — exponerade värdevaror, skyltfönster</td>
              <td>1B1 + EN 356 P2A–P4A</td>
              <td>Inbrottsfördröjning väsentlig — kompletteras med EN 356-certifiering.</td>
            </tr>
            <tr>
              <td>Bank, apotek, juvelerare</td>
              <td>1B1 + EN 356 P5A–P8B</td>
              <td>Förhöjd hotbild, ofta krav från försäkringsbolag.</td>
            </tr>
            <tr>
              <td>Högriskobjekt (ambassad, vapenförvaring, datacenter)</td>
              <td>1B1 + EN 356 P6B–P8B + karmförankring</td>
              <td>Penetrationsförsök med tunga verktyg ska fördröjas.</td>
            </tr>
          </tbody>
        </ResponsiveTable>

        <h2>När behövs andra standarder än EN 12600?</h2>
        <p>
          EN 12600 är personsäkerhetsstandard. Om hotbilden är riktad attack med
          verktyg, sprängning eller ballistiskt skydd behövs andra standarder utöver
          EN 12600:
        </p>
        <CheckList
          items={[
            "EN 356 (P1A–P8B) — manuell attack med slägga, yxa eller liknande verktyg. Klassar inbrottsfördröjning.",
            "EN 1063 (BR1–BR7) — ballistiskt skydd mot projektiler.",
            "EN 13541 (ER1–ER4) — sprängskydd mot tryckvåg från explosion.",
            "EN 1364 / EN 357 — brandmotstånd och brandklassat glas.",
            "EN ISO 10140 — luftljudsisolering (akustik).",
          ]}
        />

        <Callout variant="warning" title="Klassningen gäller glas och film tillsammans">
          <p>
            En film som uppnår klass 2B2 på 4 mm floatglas behöver inte uppnå samma klass på
            6 mm härdat glas eller i isolerruta. Klassningen är alltid kopplad till
            <em> filmen monterad på det specifika glaset enligt tillverkarens testprotokoll</em>.
            Be alltid om monteringsanvisning och testprotokoll innan en klass utlovas i en
            offert eller projektering.
          </p>
        </Callout>

        <h2>Sammanfattning</h2>
        <CheckList
          items={[
            "EN 12600 mäter glasets beteende vid mänsklig kollision — inte inbrott, inte brand, inte sprängning.",
            "Klassningen läses som siffra-bokstav-siffra: fallhöjd, brottmönster, fallhöjd igen.",
            "Klass 2B2 är praxis för glaspartier i kollisionszon i kontor, skola, vård och butik.",
            "Klass 1B1 är högsta nivån — krävs i butik med värdevaror, bank, högriskobjekt.",
            "Säkerhetsfilm uppgraderar typiskt vanligt floatglas till klass 2B2; för 1B1 krävs kraftigare film och ofta karmförankring.",
            "EN 12600 ersätter inte EN 356 — behövs inbrottsskydd ska båda klassningarna verifieras.",
          ]}
        />
      </GuideBody>
      <GuideFaq items={meta.faqs ?? []} />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
